import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ButtonControl, DialogControl } from "spark-core-dx/components";
import AIPanel from "../../../components/AIChat/AIPanel";
import {
  clearAiPanelSearchParams,
  dispatchAiPanelCommand,
  readAiPanelDescriptorFromSearch
} from "../../../components/AIChat/aiPanelCommand";
import ResourceMaturityBadge from "../../../components/ResourceMaturityBadge";
import { useResourceDomainContext } from "../ResourceDomainProvider";
import {
  RESOURCE_NAVIGATION_TARGET_KEY,
  RESOURCES_BASE_PATH,
  buildResourceConfigurationManagePath,
  buildResourceConfigurationWorkspaceScreenPath,
  buildResourceConfigurePath
} from "../resourceRoutes";
import {
  MCP_TOOLS_OVERVIEW_SCREEN_KEY,
  isClientMcpToolsWizard
} from "../workspace/resourceWorkspace.mcpToolsUtils";
import { Operations } from "../../../services/ItemServices";
import "./resourceConfigurationWizard.scss";
import WizardFooter from "./wizard/WizardFooter";
import WizardProgress from "./wizard/WizardProgress";
import WizardSectionNav from "./wizard/WizardSectionNav";
import WizardSingleSectionStatus from "./wizard/WizardSingleSectionStatus";
import WizardStepPanel from "./wizard/WizardStepPanel";
import {
  buildOperationPayload,
  buildResourceOperationQueryParams,
  buildServerFailureError,
  buildSourceObjectManagePanelContext,
  buildWizardAiPanelContext,
  getCurrentSectionStepKey,
  getCurrentStepKey,
  getDisplayName,
  getErrorSummary,
  getId,
  getLocalWizardNavigationTarget,
  getRunSectionStepInputValues,
  getSectionStepKey,
  getSectionStepKeyForNavigation,
  getSectionStepLabel,
  getSectionStepUiPatternKey,
  getSectionSteps,
  getStepIndex,
  getStepKey,
  getSteps,
  getValue,
  getOperationItem,
  getOperationResult,
  isActiveConfiguration,
  isFutureSectionStep,
  isFutureStep,
  isWizardValidationError,
  MARKETPLACE_RESOURCE_PANEL_KEY,
  SOURCE_OBJECT_MANAGE_PANEL_KEY,
  stepKeysEqual,
  stringifyJson
} from "./wizard/resourceConfigWizardUtils";

const bootstrapRequests = new Map();
const bootstrapResults = new Map();
const bootstrapErrors = new Map();
const routeViewStates = new Map();
const BOOTSTRAP_ERROR_CACHE_MS = 30000;
const RETRYABLE_BOOTSTRAP_ERROR_CODES = new Set(["JSE1", "JSNONJSON"]);
const SETUP_RESOURCE_NAVIGATION_ICON = "config";
const bootstrapErrorStorageKey = (bootstrapKey) => `resourceConfigWizardV2:bootstrapError:${bootstrapKey}`;
const normalizeMessageCode = (value) => String(value || "").trim().toUpperCase();

const getOperationMessageCode = (source) => {
  const result = getOperationResult(source);
  return normalizeMessageCode(
    getValue(getValue(result, "Message", "message"), "Code", "code")
    || getValue(getValue(source, "Message", "message"), "Code", "code")
  );
};

const isRetryableBootstrapError = (error) =>
  RETRYABLE_BOOTSTRAP_ERROR_CODES.has(normalizeMessageCode(error?.operationMessageCode));

const readStoredBootstrapError = (bootstrapKey) => {
  if (typeof window === "undefined" || !bootstrapKey) return "";
  try {
    const raw = window.sessionStorage.getItem(bootstrapErrorStorageKey(bootstrapKey));
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    if (!parsed?.message || Date.now() - Number(parsed.timestamp || 0) > BOOTSTRAP_ERROR_CACHE_MS) {
      window.sessionStorage.removeItem(bootstrapErrorStorageKey(bootstrapKey));
      return "";
    }
    if (RETRYABLE_BOOTSTRAP_ERROR_CODES.has(normalizeMessageCode(parsed.code))) {
      window.sessionStorage.removeItem(bootstrapErrorStorageKey(bootstrapKey));
      return "";
    }
    return parsed.message;
  } catch {
    return "";
  }
};

const writeStoredBootstrapError = (bootstrapKey, message, code = "") => {
  if (typeof window === "undefined" || !bootstrapKey || !message) return;
  try {
    window.sessionStorage.setItem(bootstrapErrorStorageKey(bootstrapKey), JSON.stringify({
      code,
      message,
      timestamp: Date.now()
    }));
  } catch {
    // Session storage is best-effort; the in-memory guard still applies.
  }
};

const clearStoredBootstrapError = (bootstrapKey) => {
  if (typeof window === "undefined" || !bootstrapKey) return;
  try {
    window.sessionStorage.removeItem(bootstrapErrorStorageKey(bootstrapKey));
  } catch {
    // No-op.
  }
};

export const clearResourceConfigWizardV2BootstrapCache = () => {
  bootstrapRequests.clear();
  bootstrapResults.clear();
  bootstrapErrors.clear();
  routeViewStates.clear();
  if (typeof window !== "undefined") {
    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith("resourceConfigWizardV2:bootstrapError:"))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch {
      // No-op.
    }
  }
};

const routeLoadKey = (clientResourceId, resourceConfigurationId, operationName, bootstrapVariant = "") =>
  [operationName, clientResourceId || "", resourceConfigurationId || "", bootstrapVariant || ""].join(":");

const buildSetupResourceNavigationPath = (clientResourceId, resourceConfigurationId) => {
  return buildResourceConfigurePath(clientResourceId, resourceConfigurationId);
};

const shouldShowValidationSummary = (error) =>
  isWizardValidationError(error)
  && !error?.suppressWizardValidationSummary
  && !error?.validationIssues?.length;

const toSearchString = (params) => {
  const text = params.toString();
  return text ? `?${text}` : "";
};

const trace = (eventName, details = {}) => {
  const debugEnabled = typeof window !== "undefined"
    && window.localStorage?.getItem("resourceWizardV2Debug") === "true";
  if (process.env.NODE_ENV !== "production" && debugEnabled) {
    let serialized = "";
    try {
      serialized = JSON.stringify(details);
    } catch {
      serialized = String(details);
    }
    console.log(`[ResourceConfigWizardV2] ${eventName} ${serialized}`);
  }
};

const isDevOrLocalEnvironment = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "dev"
    || normalized === "development"
    || normalized === "local"
    || normalized.includes("local");
};

const isTruthySearchFlag = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const readInitialStepValues = (wizard, stepKey, sectionStepKey) => {
  const savedValues = getRunSectionStepInputValues(wizard, stepKey, sectionStepKey);
  if (Object.keys(savedValues).length) return savedValues;
  const configuration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration");
  return {
    DisplayName: getDisplayName(configuration) || getValue(wizard, "ResourceName", "resourceName") || "",
    Notes: getValue(configuration, "Notes", "notes") || ""
  };
};

const areStepValuesEqual = (left = {}, right = {}) => {
  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left?.[key] === right?.[key]);
};

const getWorkflowCursor = (wizard) => {
  if (!wizard) return { stepKey: "", sectionStepKey: "" };
  const stepKey = getCurrentStepKey(wizard);
  return { stepKey, sectionStepKey: getCurrentSectionStepKey(wizard, stepKey) };
};

const isWizardComplete = (wizard) =>
  Boolean(getValue(wizard, "IsComplete", "isComplete"))
  || isActiveConfiguration(getValue(wizard, "ResourceConfiguration", "resourceConfiguration"));

const isWizardStateItem = (item) =>
  Boolean(item && typeof item === "object" && (
    getValue(item, "SetupPath", "setupPath")
    || getValue(item, "SetupRun", "setupRun")
    || getValue(item, "ClientResourceId", "clientResourceId")
    || getValue(item, "ResourceConfiguration", "resourceConfiguration")
    || getValue(item, "ResourceName", "resourceName")
  ));

const requireWizardStateItem = (item, fallbackMessage) => {
  if (isWizardStateItem(item)) return item;
  throw new Error(fallbackMessage);
};

const getInitialViewCursor = (wizard) => getWorkflowCursor(wizard);

const PurchaseReviewStep = ({ wizard, saving, onApprove }) => (
  <section className="resource-wizard__panel">
    <span className="resource-wizard__eyebrow">Purchase review</span>
    <h2>Review required before setup</h2>
    <p>{getValue(wizard, "ResourceName", "resourceName")} needs an approved entitlement before the configuration can start.</p>
    <ButtonControl
      type="ok"
      className="resource-wizard__button resource-wizard__button--primary"
      onClick={onApprove}
      disabled={saving}
      loading={saving}
    >
      Approve review
    </ButtonControl>
  </section>
);

const ConfigurationChooser = ({ wizard }) => {
  const clientResourceId = getValue(wizard, "ClientResourceId", "clientResourceId");
  const configurations = getValue(wizard, "ExistingConfigurations", "existingConfigurations") || [];
  return (
    <section className="resource-wizard__panel">
      <span className="resource-wizard__eyebrow">Configurations</span>
      <h2>Select a configuration</h2>
      <div className="resource-wizard__configuration-list">
        {configurations.map((configuration) => {
          const id = getId(configuration);
          return (
            <Link key={id} to={buildResourceConfigurePath(clientResourceId, id)}>
              <strong>{getDisplayName(configuration) || "Resource configuration"}</strong>
              <span>{String(getValue(configuration, "ConfigurationStatus", "configurationStatus") || "Configuring")}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const ResourceConfigWizard = ({ stepRegistry }) => {
  const { clientResourceId, resourceConfigurationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const resourceDomainContext = useResourceDomainContext();
  const coreContext = resourceDomainContext?.coreContext;
  const coreState = resourceDomainContext?.coreState;
  const wizardMgr = resourceDomainContext?.managers?.resourceConfigurationWizard;
  const wizardMgrReady = Boolean(wizardMgr);
  const wizardMgrRef = useRef(wizardMgr);
  const coreStateRef = useRef(coreState);
  const controllerRef = useRef(null);
  const controllerRenderSignatureRef = useRef("");
  const instanceIdRef = useRef(Math.random().toString(36).slice(2, 8));
  const forceStartupFailure = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return isDevOrLocalEnvironment(coreContext?.Launch?.Env)
      && isTruthySearchFlag(params.get("forceStartupFailure") || params.get("forceWizardStartupFail"));
  }, [coreContext?.Launch?.Env, location.search]);
  const createNewConfiguration = useMemo(() => {
    if (resourceConfigurationId) return false;
    const params = new URLSearchParams(location.search || "");
    return isTruthySearchFlag(
      params.get("new")
      || params.get("createNewConfiguration")
      || params.get("newConfiguration")
    );
  }, [location.search, resourceConfigurationId]);
  const setupResourceNavigationPath = useMemo(
    () => buildSetupResourceNavigationPath(clientResourceId, resourceConfigurationId),
    [clientResourceId, resourceConfigurationId]
  );
  const setupResourceNavigationItems = useMemo(() => {
    if (!clientResourceId || !setupResourceNavigationPath) return [];
    const activeRoutePaths = Array.from(new Set([
      setupResourceNavigationPath,
      location.pathname
    ].filter(Boolean)));

    return [{
      _id: `resource-setup-${clientResourceId}-${resourceConfigurationId || "new"}`,
      Key: `resource-setup-${clientResourceId}-${resourceConfigurationId || "new"}`,
      DisplayName: "Setup Resource",
      Icon: SETUP_RESOURCE_NAVIGATION_ICON,
      RoutePath: setupResourceNavigationPath,
      ActiveRoutePaths: activeRoutePaths,
      Sequence: 1,
      Placement: "after"
    }];
  }, [clientResourceId, location.pathname, resourceConfigurationId, setupResourceNavigationPath]);

  const operationName = resourceConfigurationId
    ? Operations.ResourceConfigurationWizardItem.LoadResourceConfigurationWizard
    : Operations.ResourceConfigurationWizardItem.StartOrResumeResourceConfiguration;
  const bootstrapVariant = [
    createNewConfiguration ? "createNewConfiguration" : "",
    forceStartupFailure ? "forceStartupFailure" : "normal"
  ].filter(Boolean).join("|");
  const bootstrapKey = routeLoadKey(
    clientResourceId,
    resourceConfigurationId,
    operationName,
    bootstrapVariant
  );
  const shouldUseCachedBootstrap = !resourceConfigurationId;
  const cachedBootstrapWizard = shouldUseCachedBootstrap ? bootstrapResults.get(bootstrapKey) || null : null;
  const profileContextReady = Boolean(coreContext?.Profile?.ClientId && coreContext?.Profile?.AppId);

  const initialCursorAppliedRef = useRef(Boolean(cachedBootstrapWizard));
  const [wizard, setWizard] = useState(() => cachedBootstrapWizard);
  const [viewCursor, setViewCursor] = useState(() => getInitialViewCursor(cachedBootstrapWizard));
  const [sectionValues, setSectionValues] = useState({});
  const [, setSavedSections] = useState(() => new Set());
  const [editConfirm, setEditConfirm] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [completeConfirmOpen, setCompleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(() => !cachedBootstrapWizard);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [, setControllerRevision] = useState(0);

  useEffect(() => {
    wizardMgrRef.current = wizardMgr;
  }, [wizardMgr]);

  useEffect(() => {
    coreStateRef.current = coreState;
  }, [coreState]);

  useEffect(() => {
    const setCustomNavigationItems = coreContext?.setCustomNavigationItems;
    if (!setCustomNavigationItems) return;

    setCustomNavigationItems(RESOURCE_NAVIGATION_TARGET_KEY, setupResourceNavigationItems);

    return () => {
      setCustomNavigationItems(RESOURCE_NAVIGATION_TARGET_KEY, []);
    };
  }, [coreContext?.setCustomNavigationItems, setupResourceNavigationItems]);

  useEffect(() => {
    const instanceId = instanceIdRef.current;
    trace("mount", { instanceId });
    return () => trace("unmount", { instanceId });
  }, []);

  const runWizardOperation = useCallback(async (name, item) => {
    const manager = wizardMgrRef.current;
    if (!manager) throw new Error("Resource wizard service is not available.");
    const result = await manager.RunOperation({
      operationName: name,
      item,
      queryParams: buildResourceOperationQueryParams({
        clientResourceId,
        resourceConfigurationId,
        item
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: true
    });
    if (result === undefined || result === null) {
      throw new Error("Resource wizard operation did not return a response. Sign in again and retry.");
    }
    const operationResult = getOperationResult(result);
    const operationSuccess = getValue(operationResult, "Success", "success");
    const rawSuccess = getValue(result, "Success", "success");
    const failed = operationSuccess === false
      || rawSuccess === false
      || String(operationSuccess).toLowerCase() === "false"
      || String(rawSuccess).toLowerCase() === "false";
    if (failed) {
      const failureSource = operationResult || result;
      const failureError = buildServerFailureError(failureSource, "Resource wizard operation failed.", coreStateRef.current);
      failureError.operationMessageCode = getOperationMessageCode(failureSource);
      throw failureError;
    }
    return getOperationItem(result);
  }, [clientResourceId, resourceConfigurationId]);

  const applyWorkflowCursor = useCallback((nextWizard) => {
    const nextCursor = getWorkflowCursor(nextWizard);
    routeViewStates.set(bootstrapKey, { viewCursor: nextCursor });
    setViewCursor(nextCursor);
    initialCursorAppliedRef.current = true;
    trace("apply workflow cursor", {
      instanceId: instanceIdRef.current,
      stepKey: nextCursor.stepKey,
      sectionStepKey: nextCursor.sectionStepKey
    });
  }, [bootstrapKey]);

  useEffect(() => {
    let disposed = false;
    if (!wizardMgrReady) {
      setLoading(true);
      return () => {
        disposed = true;
      };
    }
    const cached = shouldUseCachedBootstrap ? bootstrapResults.get(bootstrapKey) : null;
    if (!cached && !profileContextReady) {
      setLoading(true);
      setError("");
      setValidationError("");
      trace("bootstrap wait profile context", { instanceId: instanceIdRef.current, bootstrapKey, operationName });
      return () => {
        disposed = true;
      };
    }
    if (!clientResourceId) {
      setValidationError("");
      setError("Client Resource id is required.");
      setLoading(false);
      return () => {};
    }

    const payload = {
      ClientResourceId: clientResourceId,
      ResourceConfigurationId: resourceConfigurationId || "",
      ...(createNewConfiguration ? { CreateNewConfiguration: true } : {}),
      ...(forceStartupFailure ? { ForceStartupFailure: true } : {})
    };
    const cachedError = !cached ? bootstrapErrors.get(bootstrapKey) || readStoredBootstrapError(bootstrapKey) : "";
    if (cachedError) {
      setValidationError("");
      setError(cachedError);
      setLoading(false);
      trace("bootstrap cached error", { instanceId: instanceIdRef.current, bootstrapKey, operationName });
      return () => {
        disposed = true;
      };
    }

    const request = cached
      ? Promise.resolve(cached)
      : bootstrapRequests.get(bootstrapKey)
        || runWizardOperation(operationName, payload)
          .then((item) => requireWizardStateItem(item, "Resource wizard did not return state. Sign in again and retry."))
          .then((item) => {
            bootstrapResults.set(bootstrapKey, item);
            bootstrapErrors.delete(bootstrapKey);
            clearStoredBootstrapError(bootstrapKey);
            return item;
          })
          .catch((ex) => {
            const message = getErrorSummary(ex, "Resource wizard failed to load.");
            if (isRetryableBootstrapError(ex)) {
              bootstrapErrors.delete(bootstrapKey);
              clearStoredBootstrapError(bootstrapKey);
            } else {
              bootstrapErrors.set(bootstrapKey, message);
              writeStoredBootstrapError(bootstrapKey, message, ex?.operationMessageCode);
            }
            throw ex;
          })
          .finally(() => bootstrapRequests.delete(bootstrapKey));

    if (!cached && !bootstrapRequests.has(bootstrapKey)) {
      bootstrapRequests.set(bootstrapKey, request);
    }

    if (!cached) setLoading(true);
    setError("");
    setValidationError("");
    trace("bootstrap start", { instanceId: instanceIdRef.current, bootstrapKey, operationName, cached: Boolean(cached) });
    request
      .then((item) => {
        if (disposed) return;
        bootstrapErrors.delete(bootstrapKey);
        clearStoredBootstrapError(bootstrapKey);
        setWizard(item);
        if (createNewConfiguration && !resourceConfigurationId) {
          const nextResourceConfigurationId = getValue(item, "ResourceConfigurationId", "resourceConfigurationId")
            || getId(getValue(item, "ResourceConfiguration", "resourceConfiguration"));
          if (nextResourceConfigurationId) {
            navigate(
              buildSetupResourceNavigationPath(clientResourceId, nextResourceConfigurationId),
              { replace: true }
            );
          }
        }
        if (!initialCursorAppliedRef.current) applyWorkflowCursor(item);
      })
      .catch((ex) => {
        if (!disposed) {
          setValidationError("");
          setError(getErrorSummary(ex, "Resource wizard failed to load."));
        }
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, [applyWorkflowCursor, bootstrapKey, clientResourceId, createNewConfiguration, forceStartupFailure, navigate, operationName, profileContextReady, resourceConfigurationId, runWizardOperation, shouldUseCachedBootstrap, wizardMgrReady]);

  const steps = useMemo(() => getSteps(wizard), [wizard]);
  const activeStep = useMemo(
    () => steps.find((step) => stepKeysEqual(getStepKey(step), viewCursor.stepKey)) || steps[0] || null,
    [steps, viewCursor.stepKey]
  );
  const activeStepKey = getStepKey(activeStep);
  const activeStepIndex = getStepIndex(wizard, activeStepKey);
  const activeSectionSteps = useMemo(() => getSectionSteps(activeStep), [activeStep]);
  const activeSectionStep = useMemo(
    () => activeSectionSteps.find((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), viewCursor.sectionStepKey))
      || activeSectionSteps[0]
      || null,
    [activeSectionSteps, viewCursor.sectionStepKey]
  );
  const activeSectionStepKey = getSectionStepKey(activeSectionStep);
  const activeSectionStepIndex = activeSectionSteps.findIndex((sectionStep) => stepKeysEqual(getSectionStepKey(sectionStep), activeSectionStepKey));
  const activeSectionIdentity = `${activeStepKey || ""}::${activeSectionStepKey || ""}`;
  const workflowStepKey = getCurrentStepKey(wizard);
  const workflowSectionStepKey = getCurrentSectionStepKey(wizard, workflowStepKey);
  const workflowIdentity = `${workflowStepKey || ""}::${workflowSectionStepKey || ""}`;
  const resourceConfiguration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration");
  const currentResourceConfigurationId = getValue(wizard, "ResourceConfigurationId", "resourceConfigurationId") || getId(resourceConfiguration);
  const setupRunId = getValue(wizard, "SetupRunId", "setupRunId") || getId(getValue(wizard, "SetupRun", "setupRun"));
  const isComplete = isWizardComplete(wizard);
  const isClientMcpToolsSetup = isClientMcpToolsWizard(wizard);
  const clientMcpToolsManagerPath = useMemo(() => {
    if (!isClientMcpToolsSetup || !clientResourceId || !currentResourceConfigurationId) return "";
    return buildResourceConfigurationWorkspaceScreenPath(
      clientResourceId,
      currentResourceConfigurationId,
      MCP_TOOLS_OVERVIEW_SCREEN_KEY
    );
  }, [clientResourceId, currentResourceConfigurationId, isClientMcpToolsSetup]);
  const openClientMcpToolsManager = useCallback(() => {
    if (!clientMcpToolsManagerPath) return false;
    navigate(clientMcpToolsManagerPath);
    return true;
  }, [clientMcpToolsManagerPath, navigate]);
  const needsPurchaseReview = Boolean(getValue(wizard, "NeedsPurchaseReview", "needsPurchaseReview"));
  const needsConfigurationSelection = Boolean(getValue(wizard, "NeedsConfigurationSelection", "needsConfigurationSelection"));
  const wizardTitle = getValue(wizard, "ResourceName", "resourceName") || "Resource configuration";
  const showSectionStepNav = activeSectionSteps.length > 1;
  const isLastStep = activeStepIndex >= steps.length - 1 && activeSectionStepIndex >= activeSectionSteps.length - 1;
  const activePatternKey = getSectionStepUiPatternKey(activeSectionStep);
  const activeIsWorkflow = stepKeysEqual(workflowStepKey, activeStepKey)
    && stepKeysEqual(workflowSectionStepKey, activeSectionStepKey);
  const activeReadOnly = !activeIsWorkflow;
  const activeValues = useMemo(
    () => sectionValues[activeSectionIdentity] || {},
    [activeSectionIdentity, sectionValues]
  );
  const activeAiPanelDescriptor = useMemo(() => readAiPanelDescriptorFromSearch(location.search), [location.search]);
  const isExternalAiPanelOpen = activeAiPanelDescriptor?.panel === SOURCE_OBJECT_MANAGE_PANEL_KEY
    || activeAiPanelDescriptor?.panel === MARKETPLACE_RESOURCE_PANEL_KEY;
  const showWizardState = isDevOrLocalEnvironment(coreContext?.Launch?.Env);

  useEffect(() => {
    if (activeAiPanelDescriptor?.panel !== MARKETPLACE_RESOURCE_PANEL_KEY) return;
    const nextParams = clearAiPanelSearchParams(location.search);
    navigate(
      {
        pathname: location.pathname,
        search: toSearchString(nextParams),
        hash: location.hash
      },
      { replace: true }
    );
  }, [activeAiPanelDescriptor?.panel, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!wizard || !activeStep || !activeSectionStep || !activeSectionIdentity) return;
    setSectionValues((previous) => {
      const nextInitialValues = readInitialStepValues(wizard, activeStepKey, activeSectionStepKey);
      if (previous[activeSectionIdentity]) {
        if (!activeReadOnly || areStepValuesEqual(previous[activeSectionIdentity], nextInitialValues)) return previous;
        return {
          ...previous,
          [activeSectionIdentity]: nextInitialValues
        };
      }
      return {
        ...previous,
        [activeSectionIdentity]: nextInitialValues
      };
    });
  }, [activeReadOnly, activeSectionIdentity, activeSectionStep, activeSectionStepKey, activeStep, activeStepKey, wizard]);

  useEffect(() => {
    trace("view", { instanceId: instanceIdRef.current, activeStepKey, activeSectionStepKey, workflowStepKey, workflowSectionStepKey, activeReadOnly });
  }, [activeReadOnly, activeSectionStepKey, activeStepKey, workflowSectionStepKey, workflowStepKey]);

  const registerStepController = useCallback((controller) => {
    const hadController = Boolean(controllerRef.current);
    controllerRef.current = controller || null;
    if (!controller) {
      controllerRenderSignatureRef.current = "";
      if (hadController) {
        setControllerRevision((previous) => previous + 1);
      }
      return;
    }

    let canContinue = null;
    try {
      canContinue = controller.canContinue ? Boolean(controller.canContinue()) : null;
    } catch {
      canContinue = false;
    }
    const nextSignature = `${Boolean(controller.isBusy)}|${String(canContinue)}|${controller.continueLabel || ""}`;
    if (controllerRenderSignatureRef.current === nextSignature) return;

    controllerRenderSignatureRef.current = nextSignature;
    setControllerRevision((previous) => previous + 1);
  }, []);

  const setFieldValue = useCallback((fieldName, value) => {
    if (!activeSectionIdentity) return;
    setSectionValues((previous) => ({
      ...previous,
      [activeSectionIdentity]: {
        ...(previous[activeSectionIdentity] || {}),
        [fieldName]: value
      }
    }));
    setSavedSections((previous) => {
      if (!previous.has(activeSectionIdentity)) return previous;
      const next = new Set(previous);
      next.delete(activeSectionIdentity);
      return next;
    });
  }, [activeSectionIdentity]);

  const markDirty = useCallback(() => {
    setSavedSections((previous) => {
      if (!previous.has(activeSectionIdentity)) return previous;
      const next = new Set(previous);
      next.delete(activeSectionIdentity);
      return next;
    });
  }, [activeSectionIdentity]);

  const selectView = useCallback((stepKey, sectionStepKey) => {
    if (!wizard || !stepKey) return;
    if (isFutureStep(wizard, stepKey)) return;
    const resolvedSectionStepKey = sectionStepKey || getSectionStepKeyForNavigation(wizard, stepKey);
    if (isFutureSectionStep(wizard, stepKey, resolvedSectionStepKey)) return;
    setError("");
    setValidationError("");
    const nextCursor = { stepKey, sectionStepKey: resolvedSectionStepKey };
    routeViewStates.set(bootstrapKey, { viewCursor: nextCursor });
    setViewCursor(nextCursor);
    trace("select view", { instanceId: instanceIdRef.current, stepKey, sectionStepKey: resolvedSectionStepKey });
  }, [bootstrapKey, wizard]);

  const saveWizardStep = useCallback(async (stepKey, sectionStepKey, values) => {
    const item = requireWizardStateItem(await runWizardOperation(
      Operations.ResourceConfigurationWizardItem.SaveResourceConfigurationStep,
      buildOperationPayload(wizard, {
        ClientResourceId: clientResourceId,
        ResourceConfigurationId: currentResourceConfigurationId,
        SetupRunId: setupRunId,
        CurrentStepKey: workflowStepKey,
        CurrentSectionStepKey: workflowSectionStepKey,
        StepKey: stepKey,
        SectionStepKey: sectionStepKey,
        StepInputValuesJson: stringifyJson(values)
      })
    ), "Step save did not return wizard state. Sign in again and retry.");
    setWizard(item);
    bootstrapResults.set(bootstrapKey, item);
    return item;
  }, [
    bootstrapKey,
    clientResourceId,
    currentResourceConfigurationId,
    runWizardOperation,
    setupRunId,
    wizard,
    workflowSectionStepKey,
    workflowStepKey
  ]);

  const loadCurrentWizard = useCallback(async () => {
    const item = requireWizardStateItem(await runWizardOperation(
      Operations.ResourceConfigurationWizardItem.LoadResourceConfigurationWizard,
      {
        ClientResourceId: clientResourceId,
        ResourceConfigurationId: currentResourceConfigurationId || resourceConfigurationId || ""
      }
    ), "Resource wizard refresh did not return state. Sign in again and retry.");
    setWizard(item);
    bootstrapResults.set(bootstrapKey, item);
    return item;
  }, [
    bootstrapKey,
    clientResourceId,
    currentResourceConfigurationId,
    resourceConfigurationId,
    runWizardOperation
  ]);

  const prepareValues = useCallback(async (action) => {
    const controller = controllerRef.current;
    if (action === "continue" && controller?.prepareContinue) {
      return controller.prepareContinue(activeValues, { action, wizard, step: activeStep, sectionStep: activeSectionStep });
    }
    if (controller?.prepareSave) {
      return controller.prepareSave(activeValues, { action, wizard, step: activeStep, sectionStep: activeSectionStep });
    }
    return activeValues;
  }, [activeSectionStep, activeStep, activeValues, wizard]);

  const validateFields = useCallback((values, options = {}) => {
    if (options.skipStepInputValidation) return true;
    const missing = activeSectionSteps.length
      ? (getValue(activeSectionStep, "InputFields", "inputFields") || []).find((field) =>
        getValue(field, "IsRequired", "isRequired")
        && (values?.[getValue(field, "FieldName", "fieldName")] === undefined
          || values?.[getValue(field, "FieldName", "fieldName")] === null
          || String(values?.[getValue(field, "FieldName", "fieldName")]).trim() === ""))
      : null;
    if (missing) {
      setError("");
      setValidationError(`${getValue(missing, "DisplayName", "displayName", "FieldName", "fieldName")} is required.`);
      return false;
    }
    setValidationError("");
    return true;
  }, [activeSectionStep, activeSectionSteps.length]);

  const handleSave = useCallback(async () => {
    if (activeReadOnly) return false;
    setSaving(true);
    setError("");
    setValidationError("");
    try {
      const controller = controllerRef.current;
      const prepared = await prepareValues("save");
      if (!validateFields(prepared, { skipStepInputValidation: controller?.skipStepInputValidation })) return false;
      await saveWizardStep(activeStepKey, activeSectionStepKey, prepared);
      setSectionValues((previous) => ({ ...previous, [activeSectionIdentity]: prepared }));
      setSavedSections((previous) => new Set(previous).add(activeSectionIdentity));
      return true;
    } catch (ex) {
      if (ex?.wizardItem) setWizard(ex.wizardItem);
      if (shouldShowValidationSummary(ex)) {
        setError("");
        setValidationError(getErrorSummary(ex, "Step save failed."));
      } else if (!isWizardValidationError(ex)) {
        setError(getErrorSummary(ex, "Step save failed."));
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [activeReadOnly, activeSectionIdentity, activeSectionStepKey, activeStepKey, prepareValues, saveWizardStep, validateFields]);

  const completeActiveStep = useCallback(async (prepared) => {
    const item = requireWizardStateItem(await runWizardOperation(
      Operations.ResourceConfigurationWizardItem.CompleteResourceConfigurationStep,
      buildOperationPayload(wizard, {
        ClientResourceId: clientResourceId,
        ResourceConfigurationId: currentResourceConfigurationId,
        SetupRunId: setupRunId,
        CurrentStepKey: activeStepKey,
        CurrentSectionStepKey: activeSectionStepKey,
        StepKey: activeStepKey,
        SectionStepKey: activeSectionStepKey,
        StepInputValuesJson: stringifyJson(prepared)
      })
    ), "Step completion did not return wizard state. Sign in again and retry.");
    setWizard(item);
    bootstrapResults.set(bootstrapKey, item);
    applyWorkflowCursor(item);
    return item;
  }, [
    activeSectionStepKey,
    activeStepKey,
    applyWorkflowCursor,
    bootstrapKey,
    clientResourceId,
    currentResourceConfigurationId,
    runWizardOperation,
    setupRunId,
    wizard
  ]);

  const refreshWizard = useCallback(async () => {
    if (!clientResourceId) return;
    setSaving(true);
    setError("");
    setValidationError("");
    try {
      await loadCurrentWizard();
    } catch (ex) {
      setError(getErrorSummary(ex, "Resource wizard refresh failed."));
    } finally {
      setSaving(false);
    }
  }, [clientResourceId, loadCurrentWizard]);

  const goToLocalTarget = useCallback((direction) => {
    const target = getLocalWizardNavigationTarget(wizard, activeStepKey, activeSectionStepKey, direction);
    if (target) selectView(target.stepKey, target.sectionStepKey);
  }, [activeSectionStepKey, activeStepKey, selectView, wizard]);

  const handleNext = useCallback(async () => {
    if (isClientMcpToolsSetup && isComplete && openClientMcpToolsManager()) {
      return;
    }

    const target = getLocalWizardNavigationTarget(wizard, activeStepKey, activeSectionStepKey, "next");
    const isWorkflowCursor = activeSectionIdentity === workflowIdentity;
    if (activeReadOnly || !isWorkflowCursor) {
      if (target) selectView(target.stepKey, target.sectionStepKey);
      return;
    }

    setSaving(true);
    setError("");
    setValidationError("");
    try {
      const controller = controllerRef.current;
      if (controller?.canContinue && !controller.canContinue()) throw new Error("This step cannot continue yet.");
      if (controller?.confirmContinue) {
        const confirmed = await controller.confirmContinue();
        if (!confirmed) return;
      }
      const prepared = await prepareValues("continue");
      if (!validateFields(prepared, { skipStepInputValidation: controller?.skipStepInputValidation })) return;
      await completeActiveStep(prepared);
      setSectionValues((previous) => ({ ...previous, [activeSectionIdentity]: prepared }));
      setSavedSections((previous) => new Set(previous).add(activeSectionIdentity));
    } catch (ex) {
      if (ex?.wizardItem) setWizard(ex.wizardItem);
      if (shouldShowValidationSummary(ex)) {
        setError("");
        setValidationError(getErrorSummary(ex, "Step completion failed."));
      } else if (!isWizardValidationError(ex)) {
        setError(getErrorSummary(ex, "Step completion failed."));
      }
    } finally {
      setSaving(false);
    }
  }, [
    activeReadOnly,
    activeSectionIdentity,
    activeSectionStepKey,
    activeStepKey,
    completeActiveStep,
    isClientMcpToolsSetup,
    isComplete,
    openClientMcpToolsManager,
    prepareValues,
    selectView,
    validateFields,
    wizard,
    workflowIdentity
  ]);

  const completeConfiguration = useCallback(async () => {
    setSaving(true);
    setError("");
    setValidationError("");
    try {
      const controller = controllerRef.current;
      const prepared = await prepareValues("continue");
      if (!validateFields(prepared, { skipStepInputValidation: controller?.skipStepInputValidation })) return;
      const latestWizard = await loadCurrentWizard();
      const latestStepKey = getCurrentStepKey(latestWizard);
      const latestSectionStepKey = getCurrentSectionStepKey(latestWizard, latestStepKey);
      const latestIsReviewCompletionStep = stepKeysEqual(latestStepKey, "estimate-review")
        && stepKeysEqual(latestSectionStepKey, "review");
      if (!latestIsReviewCompletionStep) {
        applyWorkflowCursor(latestWizard);
        setError("");
        setValidationError("Complete the current setup step before activating this Resource Configuration.");
        return;
      }

      const savedWizard = requireWizardStateItem(await runWizardOperation(
        Operations.ResourceConfigurationWizardItem.SaveResourceConfigurationStep,
        buildOperationPayload(latestWizard, {
          ClientResourceId: clientResourceId,
          ResourceConfigurationId: getValue(latestWizard, "ResourceConfigurationId", "resourceConfigurationId") || currentResourceConfigurationId,
          SetupRunId: getValue(latestWizard, "SetupRunId", "setupRunId") || setupRunId,
          CurrentStepKey: latestStepKey,
          CurrentSectionStepKey: latestSectionStepKey,
          StepKey: latestStepKey,
          SectionStepKey: latestSectionStepKey,
          StepInputValuesJson: stringifyJson(prepared)
        })
      ), "Step save did not return wizard state. Sign in again and retry.");
      setWizard(savedWizard);
      bootstrapResults.set(bootstrapKey, savedWizard);
      const item = requireWizardStateItem(await runWizardOperation(
        Operations.ResourceConfigurationWizardItem.CompleteResourceConfiguration,
        buildOperationPayload(savedWizard, {
          ClientResourceId: clientResourceId,
          ResourceConfigurationId: getValue(savedWizard, "ResourceConfigurationId", "resourceConfigurationId") || currentResourceConfigurationId,
          SetupRunId: getValue(savedWizard, "SetupRunId", "setupRunId") || setupRunId,
          StepKey: latestStepKey,
          SectionStepKey: latestSectionStepKey,
          StepInputValuesJson: stringifyJson(prepared)
        })
      ), "Configuration completion did not return wizard state. Sign in again and retry.");
      setWizard(item);
      bootstrapResults.set(bootstrapKey, item);
      applyWorkflowCursor(item);
      if (isClientMcpToolsWizard(item)) {
        const completedConfigurationId = getValue(item, "ResourceConfigurationId", "resourceConfigurationId")
          || getId(getValue(item, "ResourceConfiguration", "resourceConfiguration"))
          || currentResourceConfigurationId;
        navigate(buildResourceConfigurationWorkspaceScreenPath(
          clientResourceId,
          completedConfigurationId,
          MCP_TOOLS_OVERVIEW_SCREEN_KEY
        ));
      }
    } catch (ex) {
      if (ex?.wizardItem) setWizard(ex.wizardItem);
      if (shouldShowValidationSummary(ex)) {
        setError("");
        setValidationError(getErrorSummary(ex, "Configuration completion failed."));
      } else if (!isWizardValidationError(ex)) {
        setError(getErrorSummary(ex, "Configuration completion failed."));
      }
    } finally {
      setSaving(false);
    }
  }, [
    applyWorkflowCursor,
    bootstrapKey,
    clientResourceId,
    currentResourceConfigurationId,
    loadCurrentWizard,
    prepareValues,
    navigate,
    runWizardOperation,
    setupRunId,
    validateFields
  ]);

  const handleComplete = useCallback(() => {
    setCompleteConfirmOpen(true);
  }, []);

  const handleCompleteConfirm = useCallback(async () => {
    setCompleteConfirmOpen(false);
    await completeConfiguration();
  }, [completeConfiguration]);

  const handleEditConfirm = useCallback(async (confirmed) => {
    const pending = editConfirm;
    setEditConfirm(null);
    if (!confirmed || !pending) return;
    setSaving(true);
    setError("");
    setValidationError("");
    try {
      const item = requireWizardStateItem(await runWizardOperation(
        Operations.ResourceConfigurationWizardItem.EditResourceConfigurationStep,
        buildOperationPayload(wizard, {
          ClientResourceId: clientResourceId,
          ResourceConfigurationId: currentResourceConfigurationId,
          SetupRunId: setupRunId,
          CurrentStepKey: workflowStepKey,
          CurrentSectionStepKey: workflowSectionStepKey,
          StepKey: pending.stepKey,
          SectionStepKey: pending.sectionStepKey
        })
      ), "Edit mode did not return wizard state. Sign in again and retry.");
      setWizard(item);
      bootstrapResults.set(bootstrapKey, item);
      applyWorkflowCursor(item);
    } catch (ex) {
      if (ex?.wizardItem) setWizard(ex.wizardItem);
      setError(getErrorSummary(ex, "Opening edit mode failed."));
    } finally {
      setSaving(false);
    }
  }, [
    applyWorkflowCursor,
    bootstrapKey,
    clientResourceId,
    currentResourceConfigurationId,
    editConfirm,
    runWizardOperation,
    setupRunId,
    wizard,
    workflowSectionStepKey,
    workflowStepKey
  ]);

  const handleOpenSourceObjectManagePanel = useCallback((sourceObject) => {
    const panelParams = buildSourceObjectManagePanelContext(sourceObject, {
      clientResourceId,
      resourceConfigurationId: currentResourceConfigurationId || resourceConfigurationId || ""
    });
    if (!panelParams) {
      setValidationError("");
      setError("Save this Source Object before opening Manage Source Object.");
      return;
    }
    const opened = dispatchAiPanelCommand(
      { AIPanel: "sourceobjectmanage" },
      { Value: panelParams }
    );
    if (!opened) {
      setValidationError("");
      setError("Source Object manager could not be opened.");
    }
  }, [clientResourceId, currentResourceConfigurationId, resourceConfigurationId]);

  const handlePurchaseReview = useCallback(async () => {
    setSaving(true);
    setError("");
    setValidationError("");
    try {
      const item = requireWizardStateItem(await runWizardOperation(
        Operations.ResourceConfigurationWizardItem.StubResourcePurchaseReview,
        buildOperationPayload(wizard, { ClientResourceId: clientResourceId })
      ), "Purchase review did not return wizard state. Sign in again and retry.");
      setWizard(item);
      bootstrapResults.set(bootstrapKey, item);
      applyWorkflowCursor(item);
    } catch (ex) {
      setError(getErrorSummary(ex, "Purchase review failed."));
    } finally {
      setSaving(false);
    }
  }, [applyWorkflowCursor, bootstrapKey, clientResourceId, runWizardOperation, wizard]);

  const handleCancelSetup = useCallback(async () => {
    setCancelConfirmOpen(false);
    setSaving(true);
    setError("");
    setValidationError("");
    try {
      await runWizardOperation(
        Operations.ResourceConfigurationWizardItem.CancelResourceConfigurationSetup,
        buildOperationPayload(wizard, {
          ClientResourceId: clientResourceId,
          ResourceConfigurationId: currentResourceConfigurationId,
          SetupRunId: setupRunId,
          StepKey: activeStepKey,
          SectionStepKey: activeSectionStepKey
        })
      );
      bootstrapResults.delete(bootstrapKey);
      routeViewStates.delete(bootstrapKey);
      navigate(RESOURCES_BASE_PATH);
    } catch (ex) {
      setError(getErrorSummary(ex, "Setup flow could not be deleted."));
    } finally {
      setSaving(false);
    }
  }, [
    activeSectionStepKey,
    activeStepKey,
    bootstrapKey,
    clientResourceId,
    currentResourceConfigurationId,
    navigate,
    runWizardOperation,
    setupRunId,
    wizard
  ]);

  const controller = controllerRef.current;
  const controllerBusy = Boolean(controller?.isBusy);
  const primaryActionLabel = controller?.continueLabel || "Next >>";
  const shouldRunActiveStepController = !activeReadOnly && activeSectionIdentity === workflowIdentity;
  const nextTarget = getLocalWizardNavigationTarget(wizard, activeStepKey, activeSectionStepKey, "next");
  const hasPrevious = Boolean(getLocalWizardNavigationTarget(wizard, activeStepKey, activeSectionStepKey, "previous"));
  const futureNextPhase = nextTarget?.stepKey
    && isFutureStep(wizard, nextTarget.stepKey)
    && activeSectionIdentity !== workflowIdentity;
  const nextDisabled = saving || !activeStep || !activeSectionStep || futureNextPhase
    || (shouldRunActiveStepController && (
      controllerBusy
      || (controller?.canContinue && !controller.canContinue())
    ));
  const showStepFooter = !loading && wizard && !needsPurchaseReview && !needsConfigurationSelection;
  const isReviewCompletionStep = stepKeysEqual(activeStepKey, "estimate-review")
    && stepKeysEqual(activeSectionStepKey, "review")
    && activeSectionIdentity === workflowIdentity
    && !activeReadOnly
    && !isComplete;
  const isCompletePhase = stepKeysEqual(activeStepKey, "complete");
  const hidePrimaryAction = isCompletePhase;
  const showSaveButton = !showSectionStepNav && !isLastStep && !isReviewCompletionStep && !hidePrimaryAction;
  const canCancelSetupFlow = Boolean(getValue(wizard, "CanCancelSetupFlow", "canCancelSetupFlow"))
    && activePatternKey === "GrantCheck"
    && activeSectionIdentity === workflowIdentity;
  const backPath = buildResourceConfigurationManagePath(clientResourceId);
  const requestEditSectionStep = (sectionStepKey) => {
    const sectionStep = activeSectionSteps.find((candidate) => stepKeysEqual(getSectionStepKey(candidate), sectionStepKey));
    setEditConfirm({
      stepKey: activeStepKey,
      sectionStepKey,
      label: getSectionStepLabel(sectionStep)
    });
  };

  const content = (
    <div className="resource-wizard">
      <header className="resource-wizard__header">
        <div className="resource-wizard__identity">
          <div className="resource-wizard__identity-topline">
            <span className="resource-wizard__eyebrow">{getValue(wizard, "ProductName", "productName") || "Marketplace resource"}</span>
            <ResourceMaturityBadge value={getValue(getValue(wizard, "ProductResource", "productResource"), "MaturityStatus", "maturityStatus")} />
          </div>
          <h1>{wizardTitle}</h1>
        </div>
        <div className="resource-wizard__header-actions">
          <div className="resource-wizard__status">
            <span>{isComplete ? "Completed" : needsPurchaseReview ? "Review required" : "Configuring"}</span>
          </div>
          <Link className="resource-wizard__back" to={backPath}>&lt;&lt; Back</Link>
        </div>
      </header>

      <div className="resource-wizard__content vertical-scrollable">
        {error ? <div className="resource-wizard__alert" role="alert">{error}</div> : null}
        {wizard && !loading && showWizardState ? (
          <div className="resource-wizard__debug-state" aria-label="Resource wizard state">
            <strong>State</strong>
            <span><strong>Workflow:</strong> {workflowStepKey || "-"} / {workflowSectionStepKey || "-"}</span>
            <span><strong>View:</strong> {activeStepKey || "-"} / {activeSectionStepKey || "-"}</span>
            <span><strong>Read only:</strong> {activeReadOnly ? "yes" : "no"}</span>
            <span><strong>Complete:</strong> {isComplete ? "yes" : "no"}</span>
          </div>
        ) : null}
        {loading ? (
          <div className="resource-wizard__state">Loading resource configuration...</div>
        ) : !wizard ? (
          <div className="resource-wizard__state">Resource configuration could not be loaded.</div>
        ) : needsPurchaseReview ? (
          <PurchaseReviewStep wizard={wizard} saving={saving} onApprove={handlePurchaseReview} />
        ) : needsConfigurationSelection ? (
          <ConfigurationChooser wizard={wizard} />
        ) : (
          <>
            <WizardProgress
              steps={steps}
              activeStepKey={activeStepKey}
              wizard={wizard}
              disabled={false}
              onSelectStep={(stepKey) => selectView(stepKey, getSectionStepKeyForNavigation(wizard, stepKey))}
            />
            <section className={`resource-wizard__body${activeStep && activeSectionStep ? "" : " resource-wizard__body--unavailable"}${showSectionStepNav ? "" : " resource-wizard__body--single-section"}`}>
              {activeStep && activeSectionStep ? (
                <>
                  {showSectionStepNav ? (
                    <WizardSectionNav
                      sectionSteps={activeSectionSteps}
                      activeSectionStepKey={activeSectionStepKey}
                      wizard={wizard}
                      stepKey={activeStepKey}
                      onSelectSectionStep={selectView}
                      onRequestEditSectionStep={isComplete ? null : requestEditSectionStep}
                    />
                  ) : null}
                  {showSectionStepNav ? (
                    <WizardStepPanel
                      wizard={wizard}
                      step={activeStep}
                      sectionStep={activeSectionStep}
                      values={activeValues}
                      validationError={validationError}
                      onChange={setFieldValue}
                      onSave={handleSave}
                      onContinue={handleNext}
                      onWizardRefresh={refreshWizard}
                      saving={saving}
                      isLastSectionStep={activeSectionStepIndex >= activeSectionSteps.length - 1}
                      registerStepController={registerStepController}
                      onOpenSourceObjectManagePanel={handleOpenSourceObjectManagePanel}
                      onDirty={markDirty}
                      readOnly={activeReadOnly}
                      registry={stepRegistry}
                    />
                  ) : (
                    <div className="resource-wizard__single-section-shell">
                      <WizardSingleSectionStatus
                        sectionSteps={activeSectionSteps}
                        activeSectionStepKey={activeSectionStepKey}
                        wizard={wizard}
                        stepKey={activeStepKey}
                        onRequestEditSectionStep={isComplete ? null : requestEditSectionStep}
                      />
                      <WizardStepPanel
                        wizard={wizard}
                        step={activeStep}
                        sectionStep={activeSectionStep}
                        values={activeValues}
                        validationError={validationError}
                        onChange={setFieldValue}
                        onSave={handleSave}
                        onContinue={handleNext}
                        onWizardRefresh={refreshWizard}
                        saving={saving}
                        isLastSectionStep={activeSectionStepIndex >= activeSectionSteps.length - 1}
                        registerStepController={registerStepController}
                        onOpenSourceObjectManagePanel={handleOpenSourceObjectManagePanel}
                        onDirty={markDirty}
                        readOnly={activeReadOnly}
                        registry={stepRegistry}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div className="resource-wizard__state resource-wizard__state--full">
                  Manual configuration is not supported by this Resource. Please contact Convoke support for assistance.
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {showStepFooter ? (
        <WizardFooter
          canCancelSetupFlow={canCancelSetupFlow}
          showPreviousStepButton={hasPrevious}
          showSaveButton={showSaveButton}
          showCompleteAction={isReviewCompletionStep}
          hidePrimaryAction={hidePrimaryAction}
          isLastStep={isLastStep}
          isComplete={isComplete}
          saving={saving}
          hasActiveSectionStep={Boolean(activeStep && activeSectionStep)}
          activeSectionStepReadOnly={activeReadOnly}
          nextDisabled={nextDisabled}
          primaryActionLabel={primaryActionLabel}
          onCancelSetup={() => setCancelConfirmOpen(true)}
          onPrevious={() => goToLocalTarget("previous")}
          onSave={handleSave}
          onNext={handleNext}
          onComplete={handleComplete}
        />
      ) : null}

      <DialogControl
        title="Edit Previous Step"
        openDialog={Boolean(editConfirm)}
        okText="Edit Step"
        cancelText="Cancel"
        onOk={() => handleEditConfirm(true)}
        onCancel={() => handleEditConfirm(false)}
        onX={() => handleEditConfirm(false)}
      >
        <p>Editing {editConfirm?.label || "this previous step"} can discard or regenerate data from later setup steps. Continue only if you need to correct this setup step and are prepared to lose downstream setup changes.</p>
      </DialogControl>
      <DialogControl
        title="Delete Setup Flow"
        openDialog={cancelConfirmOpen}
        okText="Delete Setup"
        cancelText="Keep Setup"
        onOk={handleCancelSetup}
        onCancel={() => setCancelConfirmOpen(false)}
        onX={() => setCancelConfirmOpen(false)}
      >
        <p>This will cancel this setup flow and delete the Resource Configuration created for it. If this is the only configuration for the Client Resource, the Client Resource will also be deleted. This is only allowed from Grant Check before moving to Resource Details.</p>
      </DialogControl>
      <DialogControl
        title="Complete Resource Configuration"
        openDialog={completeConfirmOpen}
        okText="Complete Setup"
        cancelText="Keep Reviewing"
        onOk={handleCompleteConfirm}
        onCancel={() => setCompleteConfirmOpen(false)}
        onX={() => setCompleteConfirmOpen(false)}
      >
        <p>Completing this setup activates the initial Resource Configuration. After it is complete, this setup wizard is no longer used for editing; future changes will happen from the Resource Management screens.</p>
      </DialogControl>
    </div>
  );

  const aiContext = buildWizardAiPanelContext({ wizard, clientResourceId, resourceConfigurationId: currentResourceConfigurationId || resourceConfigurationId });

  return (
    <div className="resource-wizard-page vertical-scrollable">
      <AIPanel
        open={true}
        presentation="inline"
        className="resource-wizard-ai-panel"
        chatPane="right"
        chatPaneCollapseMode="closed"
        chatPaneStartOpen={false}
        chatEnabled={true}
        chatPaneToggleEnabled={!isExternalAiPanelOpen}
        operationName={null}
        referenceId={aiContext.referenceId}
        referenceName={aiContext.referenceName}
        params={aiContext.params}
        payload={null}
        aiProcessingMode="Direct"
        singleSessionMode={true}
        planningPreviewEnabled={true}
        splashMessage="Ask about this resource setup."
        introMessage={null}
        minLeft={520}
        minRight={360}
      >
        {content}
      </AIPanel>
    </div>
  );
};

export default ResourceConfigWizard;
